import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const initial = {
    product_name: "",
    website_url: "",
    category_slug: "",
    description: "",
    why_indian: "",
    submitter_email: "",
    is_made_in_india: true,
    is_foreign_subsidiary: false,
    has_free_plan: false,
    is_open_source: false,
};

export default function Submit() {
    const [params] = useSearchParams();
    const [form, setForm] = useState(() => ({
        ...initial,
        category_slug: params.get("category") || "",
    }));
    const [categories, setCategories] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        api.listCategories("alpha").then(setCategories);
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.product_name.trim()) e.product_name = "Required";
        if (!form.website_url.trim()) e.website_url = "Required";
        else if (!/^https?:\/\/.+/i.test(form.website_url))
            e.website_url = "Must start with http:// or https://";
        if (!form.category_slug) e.category_slug = "Pick a category";
        if (!form.description.trim() || form.description.trim().length < 50)
            e.description = "At least 50 characters";
        if (!form.why_indian.trim() || form.why_indian.trim().length < 10)
            e.why_indian = "Tell us why this is Indian (at least 10 characters)";
        if (!form.submitter_email.trim() || !/.+@.+\..+/.test(form.submitter_email))
            e.submitter_email = "Valid email required";
        if (form.is_foreign_subsidiary)
            e.is_foreign_subsidiary =
                "BharatStack only lists Indian-founded, Indian-headquartered companies. Subsidiaries of foreign companies (e.g. GoDaddy India, HostGator India) are not eligible.";
        if (!form.is_made_in_india)
            e.is_made_in_india =
                "BharatStack only lists products founded and headquartered in India.";
        return e;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            await api.submit(form);
            setDone(true);
            toast.success("Submission received! We'll review within 48 hours.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Something went wrong. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                className="max-w-2xl mx-auto px-4 py-20 text-center"
                data-testid="submit-success"
            >
                <div className="mx-auto h-16 w-16 rounded-full bg-indiagreen-50 text-indiagreen-700 flex items-center justify-center">
                    <CheckCircle2 className="h-9 w-9" />
                </div>
                <h1 className="mt-6 font-heading font-extrabold text-3xl text-ink">
                    Thank you!
                </h1>
                <p className="mt-3 text-ink-muted text-lg">
                    We've received your submission and will verify it within
                    48 hours. We check Indian founding status carefully.
                </p>
                <div className="mt-7 flex justify-center gap-3">
                    <Link
                        to="/"
                        className="inline-flex bg-white border border-line rounded-lg px-5 py-2.5 font-semibold text-ink hover:border-saffron/40"
                    >
                        Back to home
                    </Link>
                    <button
                        onClick={() => {
                            setForm(initial);
                            setDone(false);
                        }}
                        className="btn-saffron rounded-lg px-5 py-2.5 font-semibold"
                        data-testid="submit-another"
                    >
                        Submit another
                    </button>
                </div>
            </div>
        );
    }

    const labelCls = "block text-sm font-semibold text-ink mb-1.5";
    const inputCls =
        "w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron/40";
    const errCls = "mt-1 text-xs text-red-600 font-medium";

    return (
        <div className="max-w-3xl mx-auto px-4 lg:px-6 py-12" data-testid="submit-page">
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-ink">
                Submit a Product
            </h1>
            <p className="mt-3 text-ink-muted text-lg">
                Tell us — we'll verify and add it to BharatStack.{" "}
                <strong className="text-ink">
                    We only accept Indian-founded, Indian-HQ'd companies.
                </strong>{" "}
                Subsidiaries of foreign companies are not eligible.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
                <div>
                    <label htmlFor="name" className={labelCls}>
                        Product Name *
                    </label>
                    <input
                        id="name"
                        className={inputCls}
                        value={form.product_name}
                        onChange={(e) => update("product_name", e.target.value)}
                        data-testid="submit-product-name"
                    />
                    {errors.product_name && (
                        <p className={errCls}>{errors.product_name}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="url" className={labelCls}>
                        Website URL *
                    </label>
                    <input
                        id="url"
                        type="url"
                        placeholder="https://example.com"
                        className={inputCls}
                        value={form.website_url}
                        onChange={(e) => update("website_url", e.target.value)}
                        data-testid="submit-website-url"
                    />
                    {errors.website_url && (
                        <p className={errCls}>{errors.website_url}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="cat" className={labelCls}>
                        Category *
                    </label>
                    <select
                        id="cat"
                        className={inputCls}
                        value={form.category_slug}
                        onChange={(e) =>
                            update("category_slug", e.target.value)
                        }
                        data-testid="submit-category"
                    >
                        <option value="">Select a category</option>
                        {categories.map((c) => (
                            <option key={c.slug} value={c.slug}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    {errors.category_slug && (
                        <p className={errCls}>{errors.category_slug}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="desc" className={labelCls}>
                        Short description * (min 50 chars)
                    </label>
                    <textarea
                        id="desc"
                        rows={5}
                        className={inputCls}
                        value={form.description}
                        onChange={(e) => update("description", e.target.value)}
                        data-testid="submit-description"
                    />
                    <div className="mt-1 flex items-center justify-between">
                        {errors.description ? (
                            <p className={errCls}>{errors.description}</p>
                        ) : (
                            <span />
                        )}
                        <span className="text-xs text-ink-muted">
                            {form.description.trim().length} / 50
                        </span>
                    </div>
                </div>

                <div>
                    <label htmlFor="why" className={labelCls}>
                        Why is this Indian? * (founders, HQ, ownership)
                    </label>
                    <textarea
                        id="why"
                        rows={2}
                        placeholder="e.g. Founded in Bengaluru by Indian engineers. HQ in Pune. 100% Indian-owned."
                        className={inputCls}
                        value={form.why_indian}
                        onChange={(e) => update("why_indian", e.target.value)}
                        data-testid="submit-why-indian"
                    />
                    {errors.why_indian && (
                        <p className={errCls}>{errors.why_indian}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="email" className={labelCls}>
                        Your email * (for follow-up only)
                    </label>
                    <input
                        id="email"
                        type="email"
                        className={inputCls}
                        value={form.submitter_email}
                        onChange={(e) =>
                            update("submitter_email", e.target.value)
                        }
                        data-testid="submit-email"
                    />
                    {errors.submitter_email && (
                        <p className={errCls}>{errors.submitter_email}</p>
                    )}
                </div>

                <fieldset className="bg-white border border-line rounded-xl p-5 space-y-3">
                    <legend className="text-sm font-semibold text-ink px-1">
                        Tell us more
                    </legend>

                    <div>
                        <span className={labelCls}>
                            Is this Made in India? *
                        </span>
                        <div className="flex gap-4">
                            {[
                                ["Yes", true],
                                ["No", false],
                            ].map(([label, val]) => (
                                <label
                                    key={label}
                                    className="inline-flex items-center gap-2 cursor-pointer"
                                >
                                    <input
                                        type="radio"
                                        name="india"
                                        checked={form.is_made_in_india === val}
                                        onChange={() =>
                                            update("is_made_in_india", val)
                                        }
                                        className="accent-saffron"
                                        data-testid={`submit-india-${label.toLowerCase()}`}
                                    />
                                    <span className="text-sm text-ink">
                                        {label}
                                    </span>
                                </label>
                            ))}
                        </div>
                        {errors.is_made_in_india && (
                            <p className={errCls}>{errors.is_made_in_india}</p>
                        )}
                    </div>

                    <div>
                        <span className={labelCls}>
                            Is this a subsidiary of a foreign company? *
                        </span>
                        <div className="flex gap-4">
                            {[
                                ["Yes", true],
                                ["No", false],
                            ].map(([label, val]) => (
                                <label
                                    key={label}
                                    className="inline-flex items-center gap-2 cursor-pointer"
                                >
                                    <input
                                        type="radio"
                                        name="subsidiary"
                                        checked={
                                            form.is_foreign_subsidiary === val
                                        }
                                        onChange={() =>
                                            update(
                                                "is_foreign_subsidiary",
                                                val,
                                            )
                                        }
                                        className="accent-saffron"
                                        data-testid={`submit-subsidiary-${label.toLowerCase()}`}
                                    />
                                    <span className="text-sm text-ink">
                                        {label}
                                    </span>
                                </label>
                            ))}
                        </div>
                        {errors.is_foreign_subsidiary && (
                            <p className={errCls}>
                                {errors.is_foreign_subsidiary}
                            </p>
                        )}
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.has_free_plan}
                            onChange={(e) =>
                                update("has_free_plan", e.target.checked)
                            }
                            className="accent-saffron h-4 w-4"
                            data-testid="submit-free-plan"
                        />
                        <span className="text-sm text-ink">
                            Has a Free Plan
                        </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.is_open_source}
                            onChange={(e) =>
                                update("is_open_source", e.target.checked)
                            }
                            className="accent-saffron h-4 w-4"
                            data-testid="submit-open-source"
                        />
                        <span className="text-sm text-ink">Is Open Source</span>
                    </label>
                </fieldset>

                <button
                    type="submit"
                    disabled={submitting}
                    className="btn-saffron w-full sm:w-auto rounded-xl px-6 py-3 font-semibold disabled:opacity-60"
                    data-testid="submit-form-btn"
                >
                    {submitting ? "Submitting..." : "Submit for review"}
                </button>
            </form>
        </div>
    );
}
